import { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { Download, FileSpreadsheet } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Dropzone } from '@/components/ui/Dropzone';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { Badge } from '@/components/ui/Badge';
import { Field } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { saveAs } from 'file-saver';
import { toast } from 'sonner';
import { useApp } from '@/store/app';
import { readFileAsArrayBuffer, bytesToBlob } from '@/lib/utils';

interface SheetView {
  name: string;
  rows: unknown[][];
}

export function XlsxConvert() {
  const { pendingFile, setPendingFile } = useApp();
  const [book, setBook] = useState<XLSX.WorkBook | null>(null);
  const [sheets, setSheets] = useState<SheetView[]>([]);
  const [activeSheet, setActiveSheet] = useState<string>('');
  const [filename, setFilename] = useState('workbook');

  useEffect(() => {
    if (pendingFile && /\.(xlsx?|xls|csv)$/i.test(pendingFile.name)) {
      handleFile([pendingFile]);
      setPendingFile(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleFile(files: File[]) {
    const f = files[0];
    if (!f) return;
    const buf = await readFileAsArrayBuffer(f);
    const wb = XLSX.read(buf, { type: 'array' });
    const views: SheetView[] = wb.SheetNames.map((name) => ({
      name,
      rows: XLSX.utils.sheet_to_json(wb.Sheets[name], { header: 1 }) as unknown[][],
    }));
    setBook(wb);
    setSheets(views);
    setActiveSheet(wb.SheetNames[0]);
    setFilename(f.name.replace(/\.(xlsx?|xls|csv)$/i, ''));
  }

  function exportAs(format: 'xlsx' | 'csv' | 'json' | 'tsv') {
    if (!book) return;
    if (format === 'xlsx') {
      const out = XLSX.write(book, { bookType: 'xlsx', type: 'array' });
      saveAs(bytesToBlob(new Uint8Array(out), 'application/octet-stream'), `${filename}.xlsx`);
    } else if (format === 'csv') {
      const sheet = book.Sheets[activeSheet];
      const csv = XLSX.utils.sheet_to_csv(sheet);
      saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8' }), `${filename}.csv`);
    } else if (format === 'tsv') {
      const sheet = book.Sheets[activeSheet];
      const tsv = XLSX.utils.sheet_to_csv(sheet, { FS: '\t' });
      saveAs(new Blob([tsv], { type: 'text/tab-separated-values' }), `${filename}.tsv`);
    } else {
      const sheet = book.Sheets[activeSheet];
      const json = JSON.stringify(XLSX.utils.sheet_to_json(sheet), null, 2);
      saveAs(new Blob([json], { type: 'application/json' }), `${filename}.json`);
    }
    toast.success(`Saved .${format}`);
  }

  const active = sheets.find((s) => s.name === activeSheet);
  const headers = (active?.rows[0] ?? []) as unknown[];
  const dataRows = active?.rows.slice(1) ?? [];

  return (
    <div>
      <ToolHeader
        toolId="xlsx-convert"
        title="Excel ↔ CSV ↔ JSON"
        description="Open .xlsx, .xls, or .csv. Switch sheets, preview data, export to any format."
        badges={book ? <Badge>{book.SheetNames.length} sheets</Badge> : null}
      />

      {!book ? (
        <Dropzone
          accept=".xlsx,.xls,.csv,.tsv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
          onFiles={handleFile}
          icon={<FileSpreadsheet size={20} />}
          hint="Excel (.xlsx, .xls), CSV, or TSV"
        />
      ) : (
        <div className="space-y-4">
          <Card>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-3 flex-wrap">
                <Field label="Sheet" className="!mb-0">
                  <Select
                    value={activeSheet}
                    onChange={(e) => setActiveSheet(e.target.value)}
                    options={book.SheetNames.map((s) => ({ value: s, label: s }))}
                  />
                </Field>
                <div className="text-2xs text-fg-subtle font-mono pt-5">
                  {dataRows.length} rows · {headers.length} cols
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <Button size="sm" variant="ghost" onClick={() => exportAs('csv')}>
                  <Download size={11} /> CSV
                </Button>
                <Button size="sm" variant="ghost" onClick={() => exportAs('tsv')}>
                  <Download size={11} /> TSV
                </Button>
                <Button size="sm" variant="ghost" onClick={() => exportAs('json')}>
                  <Download size={11} /> JSON
                </Button>
                <Button size="sm" variant="primary" onClick={() => exportAs('xlsx')}>
                  <Download size={11} /> XLSX
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setBook(null);
                    setSheets([]);
                  }}
                >
                  Change
                </Button>
              </div>
            </div>
          </Card>

          <Card className="!p-0 overflow-hidden">
            <div className="overflow-auto max-h-[520px]">
              <table className="w-full text-sm">
                <thead className="bg-bg-inset sticky top-0 z-10">
                  <tr>
                    <th className="px-3 py-2 text-2xs font-mono font-semibold text-fg-subtle border-b border-border w-12">
                      #
                    </th>
                    {headers.map((h, i) => (
                      <th
                        key={i}
                        className="text-left px-3 py-2 text-xs font-mono font-semibold text-fg-muted border-b border-border whitespace-nowrap"
                      >
                        {String(h ?? '')}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dataRows.slice(0, 500).map((row, i) => (
                    <tr key={i} className="hover:bg-surface-hover">
                      <td className="px-3 py-2 text-2xs font-mono text-fg-subtle border-b border-border">
                        {i + 1}
                      </td>
                      {headers.map((_, j) => (
                        <td
                          key={j}
                          className="px-3 py-2 border-b border-border whitespace-nowrap max-w-xs truncate"
                        >
                          {String(row[j] ?? '')}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {dataRows.length > 500 && (
                <div className="p-3 text-2xs text-fg-subtle text-center">
                  Showing first 500 rows · export for full data
                </div>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

export default XlsxConvert;
